import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Button,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Print as PrintIcon,
  Add as AddIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import { Payment } from '../../types/Payment';

interface MemberPaymentHistoryProps {
  memberId: string;
}

const MemberPaymentHistory: React.FC<MemberPaymentHistoryProps> = ({ memberId }) => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getMemberPayments(memberId, {
        page: page + 1,
        limit: rowsPerPage,
      });
      setPayments(response.data || []);
      setTotalCount(response.pagination?.total || 0);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  }, [memberId, page, rowsPerPage]);

  useEffect(() => {
    if (memberId) {
      fetchPayments();
    }
  }, [memberId, fetchPayments]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = (payment: Payment) => {
    setSelectedPayment(payment);
    setDetailsOpen(true);
  };

  const handleAddPayment = () => {
    // Navigate to add payment page with pre-selected member
    navigate(`/add-payment?memberId=${memberId}`);
  };

  const getPaymentMethodColor = (method: string) => {
    switch (method) {
      case 'cash': return 'success';
      case 'card': return 'primary';
      case 'check': return 'warning';
      case 'bank_transfer': return 'info';
      default: return 'default';
    }
  };

  const getPaymentTypeColor = (type: string) => {
    switch (type) {
      case 'new_membership': return 'success';
      case 'renewal': return 'primary';
      case 'upgrade': return 'info';
      case 'refund': return 'error';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'failed': return 'error';
      case 'refunded': return 'info';
      default: return 'default';
    }
  };

  const formatCurrency = (amount: number) => `R ${amount.toFixed(2)}`;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Payment History
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddPayment}
        >
          Add Payment
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Receipt #</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Method</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : payments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Box sx={{ py: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      No payments found for this member
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={handleAddPayment}
                    >
                      Add First Payment
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              payments.map((payment) => (
                <TableRow key={payment._id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ReceiptIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 16 }} />
                      <Typography variant="body2">
                        {payment.receiptNumber || 'N/A'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(payment.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={payment.paymentMethod}
                      size="small"
                      color={getPaymentMethodColor(payment.paymentMethod) as any}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={payment.paymentType.replace('_', ' ')}
                      size="small"
                      color={getPaymentTypeColor(payment.paymentType) as any}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(payment.paymentDate)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={payment.status}
                      size="small"
                      color={getStatusColor(payment.status) as any}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => handleViewDetails(payment)}>
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Print Receipt">
                      <IconButton size="small">
                        <PrintIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {payments.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </TableContainer>

      {/* Payment Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Payment Details
          {selectedPayment?.receiptNumber && ` - ${selectedPayment.receiptNumber}`}
        </DialogTitle>
        <DialogContent>
          {selectedPayment && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>Payment Information</Typography>
                <Typography variant="body2">
                  <strong>Amount:</strong> {formatCurrency(selectedPayment.amount)}
                </Typography>
                <Typography variant="body2">
                  <strong>Method:</strong> {selectedPayment.paymentMethod}
                </Typography>
                <Typography variant="body2">
                  <strong>Type:</strong> {selectedPayment.paymentType.replace('_', ' ')}
                </Typography>
                <Typography variant="body2">
                  <strong>Date:</strong> {formatDate(selectedPayment.paymentDate)}
                </Typography>
                <Typography variant="body2">
                  <strong>Status:</strong> {selectedPayment.status}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>Processing Information</Typography>
                <Typography variant="body2">
                  <strong>Processed by:</strong> {selectedPayment.processedBy.firstName} {selectedPayment.processedBy.lastName}
                </Typography>
                <Typography variant="body2">
                  <strong>Created:</strong> {formatDate(selectedPayment.createdAt)}
                </Typography>
                {selectedPayment.updatedAt !== selectedPayment.createdAt && (
                  <Typography variant="body2">
                    <strong>Updated:</strong> {formatDate(selectedPayment.updatedAt)}
                  </Typography>
                )}
              </Grid>
              {selectedPayment.membershipPeriod && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Membership Period</Typography>
                  <Typography variant="body2">
                    <strong>Start:</strong> {formatDate(selectedPayment.membershipPeriod.start)} - 
                    <strong> End:</strong> {formatDate(selectedPayment.membershipPeriod.end)}
                  </Typography>
                </Grid>
              )}
              {selectedPayment.notes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Notes</Typography>
                  <Typography variant="body2">{selectedPayment.notes}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          <Button variant="contained" startIcon={<PrintIcon />}>
            Print Receipt
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MemberPaymentHistory;
